/* Inline crash report form used by AppErrorBoundary.
   Uses raw fetch (no Convex hooks) since it renders outside ConvexProvider. */

import { useState } from "react";
import { getSessionLogs } from "@/lib/sessionLogCollector";

const CONVEX_SITE_URL = import.meta.env.VITE_CONVEX_URL as string | undefined;

interface CrashReportFormProps {
	error: Error;
	onDone: () => void;
}

export function CrashReportForm({ error, onDone }: CrashReportFormProps) {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [status, setStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");

	const handleSubmit = async () => {
		if (!CONVEX_SITE_URL || !title.trim()) return;
		setStatus("sending");
		try {
			const res = await fetch(`${CONVEX_SITE_URL}/feedback`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					type: "bug",
					title: title.trim(),
					description: description.trim(),
					route: window.location.pathname,
					userAgent: navigator.userAgent,
					errorStack: error.stack,
					sessionLogs: getSessionLogs(),
				}),
			});
			setStatus(res.ok ? "sent" : "failed");
		} catch {
			setStatus("failed");
		}
	};

	if (status === "sent") {
		return (
			<div className="rounded-md border border-border bg-background p-4 text-sm text-foreground">
				Thank you! Your bug report has been submitted.
				<div className="mt-3">
					<button
						type="button"
						onClick={() => window.location.reload()}
						className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
					>
						Refresh page
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full max-w-md space-y-3 rounded-md border border-border bg-background p-4 text-left">
			<div className="space-y-1">
				<label htmlFor="crash-title" className="text-sm font-medium text-foreground">
					Title
				</label>
				<input
					id="crash-title"
					type="text"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder="What went wrong?"
					className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
				/>
			</div>
			<div className="space-y-1">
				<label htmlFor="crash-desc" className="text-sm font-medium text-foreground">
					Additional context (optional)
				</label>
				<textarea
					id="crash-desc"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					rows={3}
					placeholder="What were you doing when this happened?"
					className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
				/>
			</div>
			<div className="flex gap-2 justify-end">
				<button
					type="button"
					onClick={onDone}
					className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={handleSubmit}
					disabled={status === "sending" || !title.trim()}
					className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
				>
					{status === "sending" ? "Sending..." : status === "failed" ? "Retry" : "Submit"}
				</button>
			</div>
			{status === "failed" && (
				<p className="text-xs text-destructive">Failed to submit. Please try again.</p>
			)}
		</div>
	);
}
